import type { TenantOrgPermission } from '@nadoutong/contracts';
import { hasPermission } from '../hooks/useSession.tsx';
import type { SessionUser } from '../hooks/useSession.tsx';
import type { I18nKey } from '../i18n/index.ts';

/**
 * Canonical tenant navigation (epic #897 PR 2) — data-driven, mirroring
 * the ops-console `layout/navigation.ts` shape but gated on tenant
 * PERMISSION STRINGS (`hasPermission`), not operator roles.
 *
 * Route targets and grouping match today's app: later page PRs (#897
 * PR 6–9) re-point them as the team routes land. The access group is
 * split into its three real routes (PR 5).
 *
 * `aliases` carries the legacy paths (e.g. `/keys`, `/organization`);
 * they participate in the same longest-prefix match so exactly ONE
 * item lights on those routes — including the redirect frame before a
 * `<Navigate replace>` alias lands on its canonical path.
 */
export interface NavItem {
  readonly to: string;
  readonly labelKey: I18nKey;
  readonly testId: string;
  /** Ligature name resolved by ui/Icon to a lucide glyph. */
  readonly icon: string;
  /** Any-of permission gate; omitted = visible to every signed-in user. */
  readonly permissions?: readonly TenantOrgPermission[];
  /** Legacy alias paths that light this item (longest-prefix matched). */
  readonly aliases?: readonly string[];
}

export interface NavGroup {
  readonly titleKey: I18nKey;
  readonly items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    titleKey: 'shell.nav.group.overview',
    items: [
      {
        to: '/dashboard',
        labelKey: 'shell.nav.dashboard',
        testId: 'dashboard',
        icon: 'space_dashboard',
      },
    ],
  },
  {
    titleKey: 'shell.nav.group.access',
    items: [
      {
        to: '/access/keys',
        labelKey: 'shell.nav.keys',
        testId: 'keys',
        icon: 'vpn_key',
        permissions: ['org:api_keys:read', 'org:api_keys:manage', 'member:api_keys:manage'],
        aliases: ['/keys', '/access/api-keys'],
      },
      {
        to: '/access/docs',
        labelKey: 'shell.nav.access_docs',
        testId: 'access-docs',
        icon: 'article',
        aliases: ['/access/base-url'],
      },
      {
        to: '/access/status',
        labelKey: 'shell.nav.service_status',
        testId: 'service-status',
        icon: 'health_and_safety',
        aliases: ['/access/model-availability'],
      },
    ],
  },
  {
    titleKey: 'shell.nav.group.usage_diagnostics',
    items: [
      {
        to: '/usage',
        labelKey: 'shell.nav.usage',
        testId: 'usage',
        icon: 'query_stats',
        permissions: ['org:usage:read', 'member:usage:read'],
      },
      {
        to: '/requests',
        labelKey: 'shell.nav.requests',
        testId: 'requests',
        icon: 'manage_search',
        permissions: ['org:usage:read', 'member:requests:read'],
      },
    ],
  },
  {
    titleKey: 'shell.nav.group.cost',
    items: [
      {
        to: '/billing',
        labelKey: 'shell.nav.billing',
        testId: 'billing',
        icon: 'account_balance_wallet',
        permissions: ['org:billing:read'],
      },
      {
        to: '/billing/pricing',
        labelKey: 'shell.nav.pricing',
        testId: 'pricing',
        icon: 'sell',
        aliases: ['/pricing'],
      },
    ],
  },
  {
    titleKey: 'shell.nav.group.team',
    items: [
      {
        to: '/team/members',
        labelKey: 'shell.nav.team_members',
        testId: 'team-members',
        icon: 'groups',
        permissions: ['org:members:read'],
        aliases: ['/team', '/organization', '/team/invitations'],
      },
      {
        to: '/team/permissions',
        labelKey: 'shell.nav.team_model_permissions',
        testId: 'team-permissions',
        icon: 'rule_settings',
        permissions: ['org:members:read'],
        aliases: ['/team/model-permissions'],
      },
      {
        to: '/team/units',
        labelKey: 'shell.nav.team_org_units',
        testId: 'team-units',
        icon: 'account_tree',
        permissions: ['org:members:read'],
        aliases: ['/team/org-units'],
      },
      {
        to: '/team/audit',
        labelKey: 'shell.nav.team_audit',
        testId: 'team-audit',
        icon: 'history',
        permissions: ['org:members:read'],
      },
    ],
  },
  {
    titleKey: 'shell.nav.group.settings',
    items: [
      {
        to: '/settings',
        labelKey: 'shell.nav.settings',
        testId: 'settings',
        icon: 'settings',
      },
    ],
  },
];

export const ALL_NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

function canSeeNavItem(user: SessionUser | null, item: NavItem): boolean {
  return (
    item.permissions === undefined ||
    item.permissions.some((permission) => hasPermission(user, permission))
  );
}

export function visibleNavGroups(user: SessionUser | null): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => canSeeNavItem(user, item)),
  })).filter((group) => group.items.length > 0);
}

export function firstAccessiblePath(user: SessionUser | null): string {
  return visibleNavGroups(user)[0]?.items[0]?.to ?? '/dashboard';
}

/**
 * Single source for "where am I": the longest permission-visible match
 * across nav targets AND their legacy aliases. Both the header title
 * and the sidebar highlight derive from this, so nested routes
 * (`/billing/topup`, `/team/invitations`) light exactly one item.
 */
export function titleItemForPath(user: SessionUser | null, pathname: string): NavItem | null {
  return ALL_NAV_ITEMS.filter((item) => canSeeNavItem(user, item))
    .flatMap((item) => [item.to, ...(item.aliases ?? [])].map((path) => ({ item, path })))
    .sort((a, b) => b.path.length - a.path.length)
    .find(({ path }) => pathname === path || pathname.startsWith(`${path}/`))?.item ?? null;
}
